// ============================================================================
// COURT TYPE FORM MODAL — Add / Edit Court Type (MSTR-CTYPE-001)
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { courtTypeService } from 'api/services/courtTypeService';
import type { CourtType } from 'api/types/courtType.types';

type CourtTypeStatus = 'ACTIVE' | 'INACTIVE';

interface CourtTypeFormData {
  typeCode: string;
  typeName: string;
  statusCode: CourtTypeStatus;
}

const INITIAL_FORM_DATA: CourtTypeFormData = {
  typeCode: '', typeName: '', statusCode: 'ACTIVE',
};

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
];

export interface CourtTypeFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit' | 'copy' | 'view';
  record?: CourtType | null;
  onSaved: () => void;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreModal?: (mode: 'create' | 'edit' | 'copy' | 'view', instanceId?: string, recordCode?: string) => void;
  createInstanceId?: string;
  minimizeRef?: React.RefObject<(() => void) | null>;
}

const CourtTypeFormModal: React.FC<CourtTypeFormModalProps> = ({
  isOpen, mode, record, onSaved, onClose,
  tabId, moduleName, onRestoreModal, createInstanceId, minimizeRef,
}) => {
  const { minimize } = useModalMinimize();

  const [formData, setFormData] = useState<CourtTypeFormData>(INITIAL_FORM_DATA);
  const formDataRef = useRef(formData); formDataRef.current = formData;
  const recordRef = useRef(record); recordRef.current = record;
  const initialSnapshotRef = useRef('');
  const modeRef = useRef(mode); modeRef.current = mode;
  const createInstanceIdRef = useRef(createInstanceId); createInstanceIdRef.current = createInstanceId;
  const isRestoreRef = useRef(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const getFieldError = useCallback((f: string) => fieldErrors[f], [fieldErrors]);
  const clearFieldError = useCallback((f: string) => {
    setFieldErrors(p => {
      if (!p[f]) return p;
      const n = { ...p }; delete n[f]; return n;
    });
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    if (isRestoreRef.current) { isRestoreRef.current = false; setFieldErrors({}); return; }
    const rec = recordRef.current;
    const data: CourtTypeFormData = rec
      ? {
          typeCode: rec.typeCode,
          typeName: rec.typeName,
          statusCode: (rec.statusCode === 'INACTIVE' ? 'INACTIVE' : 'ACTIVE') as CourtTypeStatus,
        }
      : { ...INITIAL_FORM_DATA };
    setFormData(data);
    initialSnapshotRef.current = JSON.stringify(data);
    setFieldErrors({});
  }, [isOpen]);

  const isDirty = useMemo(() => {
    if (!initialSnapshotRef.current) return false;
    return JSON.stringify(formData) !== initialSnapshotRef.current;
  }, [formData]);

  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, onClose]);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentFormData = formDataRef.current;
    const currentMode = modeRef.current;
    const currentCreateInstanceId = createInstanceIdRef.current;
    const currentRecord = recordRef.current;

    let modalId: string;
    if (currentMode === 'create') {
      modalId = `${tabId}-form-modal-create-${currentCreateInstanceId || 'default'}`;
    } else {
      modalId = `${tabId}-form-modal-edit-${currentRecord?.typeCode || currentFormData.typeCode || 'new'}`;
    }

    let title: string;
    if (currentMode === 'create') {
      const code = currentFormData.typeCode?.trim();
      const name = currentFormData.typeName?.trim();
      const idx = currentCreateInstanceId || '1';
      title = code ? `Add - ${code} (${idx})` : name ? `Add - ${name} (${idx})` : `Add New (${idx})`;
    } else {
      title = `Edit - ${currentFormData.typeCode || currentRecord?.typeCode || 'Court Type'}`;
    }

    const savedFormData = { ...currentFormData };
    const savedSnapshot = initialSnapshotRef.current;
    const currentIsDirty = savedSnapshot
      ? JSON.stringify(currentFormData) !== savedSnapshot
      : false;

    minimize({
      id: modalId, title, moduleName, tabId, isDirty: currentIsDirty,
      onRestore: () => {
        isRestoreRef.current = true;
        setFormData(savedFormData);
        initialSnapshotRef.current = savedSnapshot;
        if (onRestoreModal) {
          onRestoreModal(
            currentMode,
            currentMode === 'create' ? currentCreateInstanceId : undefined,
            currentMode !== 'create' ? currentRecord?.typeCode : undefined,
          );
        }
      },
    });
    onClose();
  }, [tabId, moduleName, minimize, onClose, onRestoreModal]);

  if (minimizeRef) minimizeRef.current = handleMinimize;

  const handleSaveClick = useCallback(() => {
    const errors: Record<string, string> = {};
    const code = formData.typeCode.trim();
    const name = formData.typeName.trim();
    if (!code) errors.typeCode = 'Type code is required';
    else if (code.length > 30) errors.typeCode = 'Max 30 characters';
    else if (!/^[A-Z0-9-]+$/.test(code)) errors.typeCode = 'Uppercase letters, digits, hyphens only';
    if (!name) errors.typeName = 'Type name is required';
    else if (name.length > 100) errors.typeName = 'Max 100 characters';
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      requestAnimationFrame(() => {
        const el = document.querySelector<HTMLElement>(`[data-field="${Object.keys(errors)[0]}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          el.querySelector<HTMLInputElement>('input')?.focus();
        }
      });
      return;
    }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [formData]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    setIsSaving(true);
    try {
      const code = formData.typeCode.trim().toUpperCase();
      const name = formData.typeName.trim();
      if (mode === 'create' || mode === 'copy') {
        await courtTypeService.create({ typeCode: code, typeName: name, statusCode: formData.statusCode });
        notificationService.toast.success(`Court Type "${code}" created successfully.`);
      } else if (record) {
        await courtTypeService.update(record.id, {
          typeCode: code, typeName: name, statusCode: formData.statusCode, rowVersion: record.rowVersion,
        });
        notificationService.toast.success(`Court Type "${code}" updated successfully.`);
      }
      setIsSaving(false);
      onSaved();
      onClose();
    } catch (err: any) {
      setIsSaving(false);
      const status = err?.response?.status;
      const serverMsg = err?.response?.data?.message;
      if (status === 409) {
        if (serverMsg?.toLowerCase().includes('modified')) {
          notificationService.toast.error('This court type was modified by another user. Refresh and try again.', 6000);
        } else {
          notificationService.toast.error(serverMsg || 'A court type with this code already exists.', 6000);
        }
      } else if (status === 400 && err?.response?.data?.errors?.length) {
        notificationService.toast.error((err.response.data.errors as string[]).join(' '), 6000);
      } else {
        notificationService.toast.error(serverMsg || 'Failed to save court type. Please try again.', 6000);
      }
    }
  }, [formData, mode, record, onSaved, onClose]);

  const handleDiscard = useCallback(() => {
    setDiscardConfirmOpen(false); onClose();
  }, [onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        {isSaving && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem',
              border: '3px solid var(--border-color)', borderTopColor: '#2563eb',
              borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>Saving...</span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">{mode === 'view' ? 'View Court Type' : mode === 'copy' ? 'Copy Court Type' : mode === 'create' ? 'Add Court Type' : 'Edit Court Type'}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar" disabled={isSaving}>
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={handleClose} disabled={isSaving}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        <div className="modal-content">
          <fieldset disabled={mode === 'view'} style={{ border: 'none', padding: 0, margin: 0, minWidth: 0 }}>
          <div className="form-group" data-field="typeCode">
            <label className="form-label">Type Code <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${getFieldError('typeCode') ? ' form-field-error' : ''}`}
              value={formData.typeCode}
              maxLength={30}
              placeholder="DRT, NCLT, HIGH-COURT..."
              onChange={(e) => {
                clearFieldError('typeCode');
                setFormData(p => ({ ...p, typeCode: e.target.value.toUpperCase() }));
              }}
              disabled={isSaving}
            />
            {getFieldError('typeCode') && <div className="form-error-message">{getFieldError('typeCode')}</div>}
          </div>

          <div className="form-group" data-field="typeName">
            <label className="form-label">Type Name <span className="text-danger">*</span></label>
            <input
              type="text"
              className={`form-input${getFieldError('typeName') ? ' form-field-error' : ''}`}
              value={formData.typeName}
              maxLength={100}
              placeholder="Debt Recovery Tribunal"
              onChange={(e) => {
                clearFieldError('typeName');
                setFormData(p => ({ ...p, typeName: e.target.value }));
              }}
              disabled={isSaving}
            />
            {getFieldError('typeName') && <div className="form-error-message">{getFieldError('typeName')}</div>}
          </div>

          <div className="form-group" data-field="statusCode">
            <SyutSelect
              label="Status"
              required
              options={STATUS_OPTIONS}
              value={formData.statusCode}
              onChange={(v) => setFormData(p => ({ ...p, statusCode: ((v as string) || 'ACTIVE') as CourtTypeStatus }))}
              isClearable={false}
              isDisabled={mode === 'view' || (isSaving)}
            />
          </div>
        </fieldset>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={isSaving}>{mode === 'view' ? 'Close' : 'Cancel'}</button>
          {mode !== 'view' && (<button className="btn btn-primary" onClick={handleSaveClick} disabled={isSaving}>
            {mode === 'copy' ? 'Save' : mode === 'create' ? 'Save' : 'Update'}
          </button>)}
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title={mode === 'copy' ? 'Confirm Create' : mode === 'create' ? 'Confirm Create' : 'Confirm Update'}
        message={(mode === 'create' || mode === 'copy')
          ? `Create court type "${formData.typeCode.trim().toUpperCase()}"?`
          : `Update court type "${formData.typeCode.trim().toUpperCase()}"?`}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />

      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={handleDiscard}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

export default CourtTypeFormModal;
